// The Lencore A1U Gold sound masking product supports 8 output channels in two groups of 4,
// indexed by operating platform (OP) 1 or 2. Each output can drive up to 8 loudspeakers. The
// outputs are referred to as 1-A, 1-B, 1-C, 1-D, 2-A, 2-B, 2-C, and 2-D.
//
// Each output has three sources routed to it: sound masking, music, and paging.
//
// There are volume and mute controls at each mixing point. This results in 3 x 8 = 24 control
// points. This driver presents each of these control points as a channel for volume and mute
// control.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const commandCategories = {
    mask: 1,
    music: 20,
    page: 10,
};
const channelLabels = ['a', 'b', 'c', 'd'];
const dspDetectedFilter = {
    mics: true,
    micsMuteStatus: false,
    amplifier: true,
    assistiveListeningMute: true,
    assistiveListeningMuteStatus: false,
    assistiveListeningVolume: true,
    audioInputMute: true,
    audioInputMuteStatus: false,
    audioInputVolume: true,
    overflowRoomMute: true,
    overflowRoomMuteStatus: false,
    overflowRoomVolume: true,
    soundReinforcementMute: true,
    soundReinforcementMuteStatus: false,
    soundReinforcementVolume: true,
    telephony: false,
    receiveGain: false,
    preset: false,
};
const individualGroup = 0;
const sourceGroups = {
    mask: 1,
    music: 20,
    page: 10,
};
const sources = ['mask', 'music', 'page'];
const subCommandMute = 15; // plus channel number
const subCommandAttenuation = 0; // plus channel number
const subCommandQueryAll = 10; // plus channel number
const muteBitDefs = [
    { channelLabel: 'a', source: 'mask', bit: 1 << 0 },
    { channelLabel: 'a', source: 'page', bit: 1 << 1 },
    { channelLabel: 'a', source: 'music', bit: 1 << 2 },
    { channelLabel: 'b', source: 'mask', bit: 1 << 3 },
    { channelLabel: 'b', source: 'page', bit: 1 << 4 },
    { channelLabel: 'b', source: 'music', bit: 1 << 5 },
    { channelLabel: 'c', source: 'mask', bit: 1 << 6 },
    { channelLabel: 'c', source: 'page', bit: 1 << 7 },
    { channelLabel: 'c', source: 'music', bit: 1 << 8 },
    { channelLabel: 'd', source: 'mask', bit: 1 << 9 },
    { channelLabel: 'd', source: 'page', bit: 1 << 10 },
    { channelLabel: 'd', source: 'music', bit: 1 << 11 },
];
export class Lencore {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.apiToken = '';
        this.channels = {};
        this.muteZones = {};
        this.opPolls = [];
        this.transactionId = 0;
        this.volumeZones = {};
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const channels = [];
                for (const channelLabel of channelLabels) {
                    for (const op of [1, 2, 3, 4]) {
                        for (const source of sources) {
                            channels.push({ channelLabel, op, source, filters: dspDetectedFilter });
                        }
                    }
                }
                return channels;
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.queryIfNecessary(key, 'mute', tags);
                return this.state.mutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const channel = this.channels[key];
                const zoneNumber = this.muteZones[key];
                if (!channel)
                    throw new Error(`no channel for key = ${key}`);
                const resp = (yield this.http.requestJson('post', `/rest/method/TPI/control?happn_token=${this.apiToken}`, {
                    data: {
                        parameters: {
                            OPAddress: zoneNumber ? zoneNumber : channel.op,
                            OPGroupSelect: zoneNumber
                                ? sourceGroups[channel.source]
                                : individualGroup,
                            OPCommandCat: channel.commandCategory,
                            OPSubCommand: zoneNumber
                                ? subCommandQueryAll
                                : subCommandMute + channel.channelNumber,
                            OPTransactionID: this.getTransactionId(),
                            OPDataCount: 7,
                            OPData: [value ? 1 : 0],
                        },
                    },
                    tags,
                }));
                if (((_a = resp === null || resp === void 0 ? void 0 : resp.data) === null || _a === void 0 ? void 0 : _a.OPResultCode) !== 1) {
                    this.log.error(`unexpected response: ${JSON.stringify(resp, null, 4)}`);
                    return (_b = this.state.mutes.values[key]) !== null && _b !== void 0 ? _b : false;
                }
                return value;
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.queryIfNecessary(key, 'volume', tags);
                return this.state.volumes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d;
                const channel = this.channels[key];
                const zoneNumber = this.volumeZones[key];
                if (!channel)
                    throw new Error(`no channel for key = ${key}`);
                const resp = (yield this.http.requestJson('post', `/rest/method/TPI/control?happn_token=${this.apiToken}`, {
                    data: {
                        parameters: {
                            OPAddress: zoneNumber ? zoneNumber : channel.op,
                            OPGroupSelect: zoneNumber
                                ? sourceGroups[channel.source]
                                : individualGroup,
                            OPCommandCat: channel.commandCategory,
                            OPSubCommand: zoneNumber
                                ? subCommandQueryAll
                                : subCommandAttenuation + channel.channelNumber,
                            OPTransactionID: this.getTransactionId(),
                            OPDataCount: 7,
                            OPData: [value],
                        },
                    },
                    tags,
                }));
                if (((_c = resp === null || resp === void 0 ? void 0 : resp.data) === null || _c === void 0 ? void 0 : _c.OPResultCode) !== 1) {
                    this.log.error(`unexpected response: ${JSON.stringify(resp, null, 4)}`);
                    return (_d = this.state.volumes.values[key]) !== null && _d !== void 0 ? _d : false;
                }
                return value;
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (config.source === 'mask') {
                    if (info.volumeType === 'trim')
                        return { max: 73, min: 25, round: 1 };
                    return { max: 97, min: 0, round: 1 };
                }
                else {
                    if (info.volumeType === 'trim')
                        return { max: 49, min: 25, round: 1 };
                    return { max: 49, min: 0, round: 1 };
                }
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                function: this.pollOnce.bind(this),
            },
        ];
        for (const op of [1, 2, 3, 4]) {
            for (const [source, commandCategory] of Object.entries(commandCategories)) {
                this.opPolls.push({
                    op,
                    source: source,
                    isQueried: {
                        initial: false,
                        poll: false,
                    },
                    needsPoll: false,
                });
                for (let c = 0; c < channelLabels.length; c++) {
                    const channelLabel = channelLabels[c];
                    const key = this.getKey({ op, channelLabel, source: source });
                    this.channels[key] = {
                        channelLabel,
                        channelNumber: c + 1,
                        commandCategory,
                        isMuteConfigured: false,
                        isVolumeConfigured: false,
                        op,
                        source: source,
                    };
                }
            }
        }
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.muteZones = {};
            this.volumeZones = {};
            for (const channel of Object.values(this.channels)) {
                channel.isMuteConfigured = false;
                channel.isVolumeConfigured = false;
            }
            for (const opPoll of this.opPolls) {
                opPoll.isQueried.initial = false;
                opPoll.isQueried.poll = false;
                opPoll.needsPoll = false;
            }
        });
    }
    onInitialize(credentials) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const username = (_a = credentials.username) !== null && _a !== void 0 ? _a : 'comfort';
            const password = (_b = credentials.password) !== null && _b !== void 0 ? _b : 'comfort';
            try {
                const data = (yield this.http.requestJson('get', `/rest/login?username=${username}&password=${password}`));
                const message = (_c = data === null || data === void 0 ? void 0 : data.message) === null || _c === void 0 ? void 0 : _c.toLowerCase();
                const token = (_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.token;
                if (message !== 'logged in ok' || token === undefined)
                    return 'authFailed';
                this.apiToken = token;
                return 'connected';
            }
            catch (error) {
                return 'verifyFailed';
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    getKey(config) {
        return `${config.op}.${config.channelLabel}.${config.source}`;
    }
    getTransactionId() {
        this.transactionId++;
        if (this.transactionId > 255)
            this.transactionId = 1;
        return this.transactionId;
    }
    getStatus(op, source, tags) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const resp = (yield this.http.requestJson('post', `/rest/method/TPI/control?happn_token=${this.apiToken}`, {
                data: {
                    parameters: {
                        OPAddress: op,
                        OPGroupSelect: individualGroup,
                        OPCommandCat: commandCategories[source],
                        OPSubCommand: subCommandQueryAll,
                        OPTransactionID: this.getTransactionId(),
                        OPDataCount: 6,
                    },
                },
                tags,
            }));
            const data = (_a = resp.data) === null || _a === void 0 ? void 0 : _a.OPData;
            if (((_b = resp.data) === null || _b === void 0 ? void 0 : _b.OPResultCode) !== 1 || data === undefined) {
                this.log.error(`unexpected response: ${JSON.stringify(resp, null, 4)}`);
                return;
            }
            for (let i = 0; i < channelLabels.length; i++) {
                const channelLabel = channelLabels[i];
                const key = this.getKey({ op, source, channelLabel });
                const channel = this.channels[key];
                if (channel.isVolumeConfigured)
                    this.state.volumes.update(key, data[i]);
            }
            const muteBits = (data[4] << 8) | (0x00ff & data[5]);
            for (const def of muteBitDefs) {
                const key = this.getKey({ op, source: def.source, channelLabel: def.channelLabel });
                const channel = this.channels[key];
                if (channel.isMuteConfigured)
                    this.state.mutes.update(key, (muteBits & def.bit) !== 0);
            }
        });
    }
    pollOnce() {
        return __awaiter(this, void 0, void 0, function* () {
            const tags = ['lencoreStatus', 'poll'];
            const opPoll = this.opPolls.find(opPoll => opPoll.needsPoll && !opPoll.isQueried.poll);
            if (opPoll) {
                // Poll the next thing that needs to be polled.
                yield this.getStatus(opPoll.op, opPoll.source, tags);
                opPoll.isQueried.poll = true;
            }
            else {
                // Reset and start polling everything again.
                if (this.opPolls.some(opPoll => opPoll.isQueried.poll)) {
                    for (const opPoll of this.opPolls) {
                        opPoll.isQueried.poll = false;
                    }
                    yield this.pollOnce();
                }
                else {
                    // There's nothing to poll, either:
                    // 1. Nothing was configured, so we send this to prevent an idle timeout.
                    // 2. Only mutes were configured, so we send this to get their values.
                    yield this.getStatus(1, 'mask', tags);
                }
            }
        });
    }
    queryIfNecessary(key, type, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const channel = this.channels[key];
            if (type === 'mute') {
                // All 12 mutes for the op are returned in a poll, so we're good if any polls have
                // happened for the op.
                if (this.opPolls.some(onPoll => onPoll.op === channel.op && onPoll.isQueried.initial)) {
                    return;
                }
            }
            const opPoll = this.opPolls.find(opPoll => opPoll.op === channel.op && opPoll.source === channel.source);
            if (opPoll === undefined)
                throw new Error(`opPoll undefined`);
            if (type === 'volume' && opPoll.isQueried.initial)
                return;
            yield this.getStatus(opPoll.op, opPoll.source, tags);
            opPoll.isQueried.initial = true;
        });
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            const channel = this.channels[key];
            if (info.type === 'mute') {
                channel.isMuteConfigured = true;
                if ('zoneNumber' in config) {
                    this.muteZones[key] = config.zoneNumber;
                }
            }
            if (info.type === 'volume') {
                channel.isVolumeConfigured = true;
                const opPoll = this.opPolls.find(opPoll => opPoll.op === channel.op && opPoll.source === channel.source);
                if (opPoll === undefined)
                    throw new Error(`opPoll undefined`);
                opPoll.needsPoll = true;
                if ('zoneNumber' in config) {
                    this.volumeZones[key] = config.zoneNumber;
                }
            }
            return 'ok';
        });
    }
}
// static properties
Lencore.channelsConfigName = 'lencoreControlChannels';
Lencore.driverType = 'httpDriver';
Lencore.key = '-MSwZEMSYwDw35Wsje6M';
Lencore.httpRequestOptions = {
    prettyCommands: true,
    scheme: 'http',
};
